/**
 * Component: Engraving Line Items script
 * ------------------------------------------------------------------------------
 * A snippet to display the cart value required to receive a free engraving
 *
 * @namespace engravingLineItems
 */
import * as cart from '@shopify/theme-cart';
import { formatMoney } from '@shopify/theme-currency';
import axios from 'axios';

/**
 * Create a new engraving with purchase object.
 */
export default () => {

  /**
   * Initialise component.
   */
  function init() {
    setEventBusListeners();
  }

  /**
   * Set listeners to update on every cart change.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('AjaxCart:updated', (response) => update(response));
  }

  /**
   * Update engraving with purchase notification.
   * @param {Object} cartData - Cart Data.
   */
  function update(cartData) {
    handleEngraving(cartData);

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }
  }


  /**
   * Checks whether a engraving should be added or removed from the cart
   * @param {Object} cartData - cart data
   */
  function handleEngraving(cartData) {
    const quo = getEngravingQty(cartData);
    const engravingToAdd = [];
    let engravingLineItemsInCart = checkEngravingsInCart(cartData);
    engravingLineItemsInCart

    if (engravingToAdd && engravingLineItemsInCart.length) {
      if (quo !== engravingLineItemsInCart[0].quantity) {
        updateEngravings(cartData)
      }

    } else {
      addEngravings(cartData, engravingToAdd);
    }

  }

  /**
   * Checks how many gifts are present in the cart
   * @param {Object} cartData - cart data
   * @returns {Array} - array of engraving line items in the cart.
   */
  function checkEngravingsInCart(cartData) {
    const engravingsInCart = [];
    const engravingLineItem = checkEngravingAdded(cartData, 1);

    if (engravingLineItem) {
      engravingsInCart.push(engravingLineItem);
    }

    return engravingsInCart;
  }

  /**
   * Add multiple gifts to the cart at once.
   * @param {Object} cartData - cart data
   * @param {Array} engravingToAdd - Array of gifts to add to the cart
   */
  function addEngravings(cartData, engravingToAdd) {
    const filteredEngravingsToAdd = engravingToAdd.filter((engraving) => {
      return !checkEngravingAdded(cartData, engraving);
    });

    if (!filteredEngravingsToAdd.length) {
      return;
    }

    const addItemsArray = filteredEngravingsToAdd.map((engraving) => {
      const engravingId = getItemId();
      const engravingQty = getEngravingQty(cartData);

      return {
        id: engravingId,
        quantity: engravingQty,
        properties: {
          '_engraving-service': 'Yes',
        },
      };
    });


    axios.post('/cart/add.js', { items: addItemsArray })
      .then((addResponses) => {
        Frame.EventBus.emit('AjaxCart:freeGiftAdded', addResponses[addResponses.length - 1]);
        return addResponses;
      })
      .catch((error) => {
        throw new Error('Could not add gifts to the cart', error);
      });
  }

  /**
* Add multiple gifts to the cart at once.
* @param {Object} cartData - cart data
* @param {Array} engravingToAdd - Array of gifts to add to the cart
*/
  function updateEngravings(cartData) {
    const engravingId = getItemId();
    const engravingQty = getEngravingQty(cartData);
    var addItemsArray = {}

    addItemsArray[engravingId] = engravingQty;


    axios.post('/cart/update.js', { updates: addItemsArray })
      .then((addResponses) => {
        Frame.EventBus.emit('AjaxCart:engravingAdded', addResponses[addResponses.length - 1]);
        return addResponses;
      })
      .catch((error) => {
        throw new Error('Could not add engravings to the cart', error);
      });
  }



  /**
   * Removes the engraving passed in as a param if it is present in the cart
   * @param {Object} cartData - cart data
   * @param {Number} engraving
   * @returns response
   */
  function removeEngraving(cartData, engraving) {
    try {
      const engravingLineItem = checkEngravingAdded(cartData, engraving);

      if (!engravingLineItem) {
        return;
      }

      const cartLineItemElement = document.querySelector(`.ajax-cart__line-item[data-key="${engravingLineItem.key}"]`);

      Frame.EventBus.emit(
        'AjaxCart:removingItem',
        cartLineItemElement,
      );

      cart.removeItem(engravingLineItem.key)
        .then((response) => {
          Frame.EventBus.emit('AjaxCart:itemRemoved', response);
          return response;
        }).catch(() => {
          throw new Error('could not remove engraving from cart');
        });
    } catch (error) {
      throw new Error('Could not check if engraving is in cart', error);
    }
  }

  /**
   * Remove multiple line items from the cart.
   * @param {Array} lineItemsToRemove - Array of line item objects to remove
   */
  function removeEngravings(lineItemsToRemove) {
    const removeRequests = lineItemsToRemove
      .map((lineItem) => {
        const cartLineItemElement = document.querySelector(`.ajax-cart__line-item[data-key="${lineItem.key}"]`);
        Frame.EventBus.emit('AjaxCart:removingItem', cartLineItemElement);
        return cart.removeItem(lineItem.key);
      });

    Promise.all(removeRequests)
      .then((removeResponses) => {
        Frame.EventBus.emit('AjaxCart:itemRemoved', removeResponses[removeResponses.length - 1]);
        return removeResponses;
      })
      .catch((error) => {
        throw new Error('Could not remove engraving from cart', error);
      });
  }

  /**
   * Gets the quantity based on item number
   * @param {Number} engraving
   * @returns Number item qty
   */
  function getEngravingQty(cartData) {
    let engravingCount = 0;
    cartData.items.forEach(function(item) {
      if (item.properties !== null) {
        if (item.properties.ENGRAVING !== undefined) {
            engravingCount++;
        }
      }
    });
    return engravingCount;
}

  /**
   * Checks if the engraving passed in as a param is present in the cart
   * @param {Object} cartData - cart data
   * @param {Number} engraving
   * @returns {Object} line item
   */
  function checkEngravingAdded(cartData, engraving) {
    const engravingId = theme.engraving.product;

    return cartData.items.find((item) => {
      const matchingVariantId = (item.variant_id === engravingId);
      const hasEngravingProperty = (item.properties && (item.properties['_engraving-service'] === 'Yes'));

      return matchingVariantId && hasEngravingProperty;
    });
  }


  /**
   * Get free engraving id value.
   * @returns {Number}
   */
  function getItemId() {
    return theme.engraving.product;
  }

  /**
   * Validate that engraving quantities are correct.
   * - Check the engraving quantities are correct.
   * - Update cart and refresh page if either engraving needs updating.
   */
  function validateCheckoutCart() {
    cart.getState()
      .then((cartData) => {
        const engravingLineItem = cartData.items.find((item) => {
          return item.properties && (item.properties['_engraving-service'] === 'Yes');

        });

        if (!engravingLineItem) {
          return;
        }

        const lineItemsToUpdate = [];

        const quo = getEngravingQty(cartData);

        if (engravingLineItem && engravingLineItem.quantity < quo || engravingLineItem && engravingLineItem.quantity > quo) {
          lineItemsToUpdate.push({
            key: engravingLineItem.key,
            quantity: quo,
          });
        }

        if (lineItemsToUpdate.length) {
          updateEngravingLineItems(lineItemsToUpdate, true);
        }
      })
      .catch((error) => {
        throw new Error('Could not get cart data', error);
      });
  }



  /**
   * Update engraving line items.
   * - Reloads the page once request is finished.
   */
  function updateEngravingLineItems(lineItemsToUpdate, refreshPage = false) {
    const updateRequests = lineItemsToUpdate.map((item) => {
      return cart.updateItem(item.key, { quantity: item.quantity });
    });

    Promise.all(updateRequests)
      .then(() => {
        if (!refreshPage) return;

        window.location.reload();
      })
      .catch((error) => {
        throw new Error('Could not update engraving line items', error);
      });
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    validateCheckoutCart,
    updateEngravingLineItems
  });
};
